import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deleteOpenAIFile, deleteVectorStoreFile, DIRECTUS_DATASETS_ENDPOINT, DIRECTUS_INSTANCE, getVectorStoreIdForWorkspace, listOpenAIFiles, cancelVectorStoreBatch } from '../../api';
import { DocumentTextIcon, GlobeAltIcon, CodeBracketIcon, ArrowDownTrayIcon, PencilIcon, XMarkIcon, PlusIcon, LockClosedIcon, ListBulletIcon, EyeIcon, EyeSlashIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Fragment } from 'react';
import axios from 'axios';
import TopBar from '../../layouts/TopBar/topBar';
import { DeleteDatasetConfirmationModal } from '../../ui/atoms/Modal/Modal';
import { formatDisplayDateTime } from '../../utils/dateFormatting';
import useDatasetProviders from '../../hooks/useDatasetProviders';
import { Select, Button, Snackbar } from '../../ui/atoms';
import StandardTable from '../../ui/molecules/StandardTable';

function DatasetDetails() {
  const { t } = useTranslation();
  const [dataset, setDataset] = useState(null);
  const [openAIFiles, setOpenAIFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newDatasetName, setNewDatasetName] = useState('');
  const [isEditingApiConfig, setIsEditingApiConfig] = useState(false);
  const [editedApiConfig, setEditedApiConfig] = useState(null);
  const [isEditingWpConfig, setIsEditingWpConfig] = useState(false);
  const [editedWpConfig, setEditedWpConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('params');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [snackbarState, setSnackbarState] = useState(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const SECRET_PLACEHOLDER = '••••••••••••';
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('directus_token');
  const { getAvailabilityLabel, getProviderLabel } = useDatasetProviders();

  const tabs = [
    { id: 'params', name: 'Params', icon: DocumentTextIcon },
    { id: 'auth', name: 'Authorization', icon: LockClosedIcon },
    { id: 'headers', name: 'Headers', icon: ListBulletIcon }
  ];

  useEffect(() => {
    const fetchDatasetDetails = async () => {
      try {
        setLoading(true);

        // Fetch Directus dataset data
        const datasetResponse = await axios.get(`${DIRECTUS_DATASETS_ENDPOINT}/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            fields: [
              '*',
              'user_created.first_name',
              'user_created.last_name',
              'user_created.email',
              'file.directus_files_id.id',
              'file.directus_files_id.filename_download',
              'file.directus_files_id.title',
              'file.directus_files_id.filesize',
              'file.directus_files_id.type'
            ]
          }
        });

        setDataset(datasetResponse.data.data);
        setNewDatasetName(datasetResponse.data.data.name);

        // Only fetch OpenAI files for file_upload datasets or datasets with files but no type set
        const shouldFetchOpenAIFiles = (
          datasetResponse.data.data.dataset_type === 'file_upload' ||
          (!datasetResponse.data.data.dataset_type && datasetResponse.data.data.file && datasetResponse.data.data.file.length > 0) ||
          (datasetResponse.data.data.dataset_type === null && datasetResponse.data.data.file && datasetResponse.data.data.file.length > 0)
        ) && datasetResponse.data.data.file && datasetResponse.data.data.file.length > 0;

        if (shouldFetchOpenAIFiles) {
          const openAIFilesPromises = datasetResponse.data.data.file.map(async (fileRelation) => {
            const filename = fileRelation.directus_files_id.filename_download;

            try {
              // Get all OpenAI files and find by filename (don't use Directus ID as OpenAI ID)
              const allFiles = await listOpenAIFiles();
              // Handle case where listOpenAIFiles returns empty array or object with data property
              const filesArray = Array.isArray(allFiles) ? allFiles : (allFiles?.data || []);

              // Only log warning if we actually got files but didn't find a match
              // (if filesArray is empty, it means API key is invalid, so don't spam warnings)
              if (filesArray.length === 0) {
                // API key is invalid or no files - silently skip
                return null;
              }

              const matchingFile = filesArray.find(file => file && file.filename === filename);

              if (matchingFile) {
                return matchingFile;
              } else {
                // Only log if we have files but didn't find match (not just invalid API key)
                if (window.location.hostname === 'localhost') {
                  console.warn(`Could not find OpenAI file for filename: ${filename}`);
                }
                return null;
              }
            } catch (error) {
              if (window.location.hostname === 'localhost') {
                console.warn(`Could not fetch OpenAI file for filename: ${filename}`, error);
              }
              return null;
            }
          });

          const resolvedOpenAIFiles = await Promise.all(openAIFilesPromises);
          setOpenAIFiles(resolvedOpenAIFiles.filter(file => file !== null));
        }

        if (window.location.hostname === 'localhost') {
          console.log('Dataset details:', datasetResponse.data.data);
        }

      } catch (error) {
        console.error('Error fetching dataset details:', error);
        setError('Failed to fetch dataset details');
      } finally {
        setLoading(false);
      }
    };

    fetchDatasetDetails();
  }, [id, token]);

  const handleUpdateName = async () => {
    try {
      await axios.patch(`${DIRECTUS_DATASETS_ENDPOINT}/${id}`, {
        name: newDatasetName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDataset(prev => ({ ...prev, name: newDatasetName }));
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating dataset name:', error);
      setError('Failed to update dataset name');
    }
  };

  const handleStartEditingApiConfig = () => {
    try {
      const apiConfig = typeof dataset.api_config === 'string'
        ? JSON.parse(dataset.api_config)
        : dataset.api_config;

      // Ensure all required fields exist
      const fullApiConfig = {
        functionName: apiConfig.functionName || '',
        description: apiConfig.description || '',
        url: apiConfig.url || '',
        method: apiConfig.method || 'GET',
        parameters: apiConfig.parameters || [],
        headers: apiConfig.headers || [],
        auth: {
          type: apiConfig.auth?.type || 'none',
          token: apiConfig.auth?.token ? SECRET_PLACEHOLDER : '',
          username: apiConfig.auth?.username || '',
          password: apiConfig.auth?.password ? SECRET_PLACEHOLDER : '',
          apiKey: apiConfig.auth?.apiKey ? SECRET_PLACEHOLDER : '',
          apiKeyHeader: apiConfig.auth?.apiKeyHeader || 'X-API-Key'
        },
        responseFormat: apiConfig.responseFormat || 'json',
        baseUrl: apiConfig.baseUrl || '',
        endpoint: apiConfig.endpoint || ''
      };

      setEditedApiConfig(fullApiConfig);
      setIsEditingApiConfig(true);
      setActiveTab('params'); // Reset to first tab
    } catch (error) {
      console.error('Error parsing API config:', error);
      setError('Failed to load API configuration for editing');
    }
  };

  const handleApiConfigChange = (field, value) => {
    setEditedApiConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAuthChange = (field, value) => {
    setEditedApiConfig(prev => ({
      ...prev,
      auth: {
        ...prev.auth,
        [field]: value
      }
    }));
  };

  const handleParameterChange = (index, field, value) => {
    setEditedApiConfig(prev => ({
      ...prev,
      parameters: prev.parameters.map((param, i) =>
        i === index ? { ...param, [field]: value } : param
      )
    }));
  };

  const addParameter = () => {
    setEditedApiConfig(prev => ({
      ...prev,
      parameters: [
        ...prev.parameters,
        { name: '', value: '', description: '', required: false }
      ]
    }));
  };

  const removeParameter = (index) => {
    setEditedApiConfig(prev => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== index)
    }));
  };

  const handleHeaderChange = (index, field, value) => {
    setEditedApiConfig(prev => ({
      ...prev,
      headers: prev.headers.map((header, i) =>
        i === index ? { ...header, [field]: value } : header
      )
    }));
  };

  const addHeader = () => {
    setEditedApiConfig(prev => ({
      ...prev,
      headers: [
        ...prev.headers,
        { name: '', value: '' }
      ]
    }));
  };

  const removeHeader = (index) => {
    setEditedApiConfig(prev => ({
      ...prev,
      headers: prev.headers.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateApiConfig = async () => {
    try {
      setLoading(true);

      // Update URL parsing
      if (editedApiConfig.url) {
        try {
          const urlObj = new URL(editedApiConfig.url);
          editedApiConfig.baseUrl = `${urlObj.protocol}//${urlObj.host}`;
          editedApiConfig.endpoint = `${urlObj.pathname}${urlObj.search}`;
        } catch (urlError) {
          console.warn('Could not parse URL for baseUrl/endpoint extraction');
        }
      }

      // Prepare deep copy for modification
      const configToUpdate = JSON.parse(JSON.stringify(editedApiConfig));

      // Implement Write-Only logic: Remove placeholder values so they aren't saved
      if (configToUpdate.auth) {
        if (configToUpdate.auth.token === SECRET_PLACEHOLDER) delete configToUpdate.auth.token;
        if (configToUpdate.auth.password === SECRET_PLACEHOLDER) delete configToUpdate.auth.password;
        if (configToUpdate.auth.apiKey === SECRET_PLACEHOLDER) delete configToUpdate.auth.apiKey;
      }

      await axios.patch(`${DIRECTUS_DATASETS_ENDPOINT}/${id}`, {
        api_config: configToUpdate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDataset(prev => ({ ...prev, api_config: editedApiConfig }));
      setIsEditingApiConfig(false);
      setEditedApiConfig(null);
    } catch (error) {
      console.error('Error updating API config:', error);
      setError('Failed to update API configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEditingApiConfig = () => {
    setIsEditingApiConfig(false);
    setEditedApiConfig(null);
  };

  const handleStartEditingWpConfig = () => {
    try {
      const wpConfig = typeof dataset.wp_config === 'string'
        ? JSON.parse(dataset.wp_config)
        : dataset.wp_config;

      const fullWpConfig = {
        ...wpConfig,
        applicationPassword: wpConfig.applicationPassword ? SECRET_PLACEHOLDER : ''
      };

      setEditedWpConfig(fullWpConfig);
      setIsEditingWpConfig(true);
    } catch (error) {
      console.error('Error parsing WP config:', error);
      setError('Failed to load WordPress configuration for editing');
    }
  };

  const handleUpdateWpConfig = async () => {
    try {
      setLoading(true);
      const configToUpdate = JSON.parse(JSON.stringify(editedWpConfig));

      if (configToUpdate.applicationPassword === SECRET_PLACEHOLDER) {
        delete configToUpdate.applicationPassword;
      }

      await axios.patch(`${DIRECTUS_DATASETS_ENDPOINT}/${id}`, {
        wp_config: JSON.stringify(configToUpdate)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDataset(prev => ({ ...prev, wp_config: JSON.stringify(editedWpConfig) }));
      setIsEditingWpConfig(false);
      setEditedWpConfig(null);
    } catch (error) {
      console.error('Error updating WP config:', error);
      setError('Failed to update WordPress configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEditingWpConfig = () => {
    setIsEditingWpConfig(false);
    setEditedWpConfig(null);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const isLocalhost = window.location.hostname === 'localhost';

      // Check if this is a file dataset that might have OpenAI files
      const isFileDatasetForDelete = (
        dataset.dataset_type === 'file_upload' ||
        (!dataset.dataset_type && dataset.file?.length > 0) ||
        (dataset.dataset_type === null && dataset.file?.length > 0) ||
        (Array.isArray(dataset.openai_file_ids) && dataset.openai_file_ids.length > 0) ||
        (typeof dataset.openai_file_ids === 'string' && dataset.openai_file_ids.trim() !== '')
      );

      if (isLocalhost) {
        console.log('🔍 DEBUGGING DELETE PROCESS');
        console.log('Dataset:', dataset);
        console.log('OpenAI Files array:', openAIFiles);
        console.log('Is file dataset?', isFileDatasetForDelete);
      }

      let openAIDeleteSuccess = true;

      const isOpenAIFileNotFoundError = (error) => {
        const message = String(error?.message || '').toLowerCase();
        return message.includes('404')
          || message.includes('not found')
          || message.includes('no such file');
      };

      const deleteOpenAIFileWithFallback = async (fileId, vectorStoreId) => {
        try {
          return await deleteVectorStoreFile(fileId, vectorStoreId);
        } catch (vectorStoreDeleteError) {
          if (isOpenAIFileNotFoundError(vectorStoreDeleteError)) {
            return { alreadyGone: true };
          }

          try {
            return await deleteOpenAIFile(fileId);
          } catch (directDeleteError) {
            if (isOpenAIFileNotFoundError(directDeleteError)) {
              return { alreadyGone: true };
            }
            throw directDeleteError;
          }
        }
      };

      // STEP 1: Delete from OpenAI first (if it's a file dataset)
      if (isFileDatasetForDelete) {
        if (isLocalhost) {
          console.log('🗑️ STEP 1: Deleting from OpenAI...');
        }

        const workspaceId = typeof dataset?.linked_workspace === 'object'
          ? dataset.linked_workspace?.id
          : dataset?.linked_workspace;
        const vectorStoreId = await getVectorStoreIdForWorkspace(workspaceId);

        // Cancel in-progress batch before deleting files (required by OpenAI API)
        if (dataset.vector_store_batch_id) {
          try {
            if (isLocalhost) {
              console.log('Cancelling vector store batch before file deletion:', dataset.vector_store_batch_id);
            }
            await cancelVectorStoreBatch(vectorStoreId, dataset.vector_store_batch_id);
            await new Promise(resolve => setTimeout(resolve, 1500));
          } catch (error) {
            if (isLocalhost) {
              console.warn('Could not cancel vector store batch (often normal if already completed):', error);
            }
          }
        }

        // Primary source of truth: dataset.openai_file_ids (stored during upload)
        const storedOpenAiFileIds = (() => {
          const raw = dataset?.openai_file_ids;
          if (Array.isArray(raw)) return raw.filter(Boolean);
          if (typeof raw === 'string') {
            try {
              const parsed = JSON.parse(raw);
              return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
            } catch (_) {
              return raw.split(',').map(v => v.trim()).filter(Boolean);
            }
          }
          return [];
        })();

        // Secondary source: already resolved files in UI
        const idsFromResolvedFiles = (openAIFiles || [])
          .map(file => file?.id)
          .filter(Boolean);

        const idsToDelete = Array.from(new Set([
          ...storedOpenAiFileIds,
          ...idsFromResolvedFiles
        ]));

        if (isLocalhost) {
          console.log('🧩 OpenAI IDs gathered for deletion:', idsToDelete);
          console.log('📦 Using vector store:', vectorStoreId);
        }

        // If no explicit IDs, fallback once to filename matching.
        let allOpenAIFilesBefore = [];
        if (idsToDelete.length === 0) {
          try {
            const allOpenAIFiles = await listOpenAIFiles();
            allOpenAIFilesBefore = Array.isArray(allOpenAIFiles) ? allOpenAIFiles : (allOpenAIFiles?.data || []);
            if (isLocalhost) {
              console.log('📋 All OpenAI files before deletion:', allOpenAIFilesBefore);
            }
          } catch (error) {
            if (isLocalhost) {
              console.warn('Could not list OpenAI files for filename fallback:', error);
            }
          }
        }

        const fileIdsToProcess = idsToDelete.length > 0
          ? idsToDelete
          : (dataset.file || [])
            .map(fileRelation => {
              const filename = fileRelation.directus_files_id?.filename_download;
              if (!filename) return null;
              const matchingFile = allOpenAIFilesBefore.find(file => file.filename === filename);
              return matchingFile?.id || null;
            })
            .filter(Boolean);

        for (const fileId of fileIdsToProcess) {
          try {
            const deleteResult = await deleteOpenAIFileWithFallback(fileId, vectorStoreId);
            if (isLocalhost) {
              console.log('✅ OpenAI file deletion result:', fileId, deleteResult);
            }
          } catch (error) {
            if (isLocalhost) {
              console.error('❌ Failed to delete OpenAI file:', fileId, error);
            }
            openAIDeleteSuccess = false;
          }
        }

        // Verify cleanup: only block Directus delete if files still exist in OpenAI
        if (!openAIDeleteSuccess && fileIdsToProcess.length > 0) {
          try {
            const allOpenAIFilesAfter = await listOpenAIFiles();
            const filesArrayAfter = Array.isArray(allOpenAIFilesAfter) ? allOpenAIFilesAfter : (allOpenAIFilesAfter?.data || []);
            const remainingIds = fileIdsToProcess.filter(id => filesArrayAfter.some(file => file.id === id));

            if (remainingIds.length === 0) {
              openAIDeleteSuccess = true;
              if (isLocalhost) {
                console.log('✅ OpenAI files no longer present — proceeding with dataset deletion');
              }
            } else if (isLocalhost) {
              console.warn('⚠️ Files still present in OpenAI after deletion attempt:', remainingIds);
            }
          } catch (error) {
            if (isLocalhost) {
              console.warn('Could not verify OpenAI file cleanup after deletion');
            }
          }
        }

        if (isLocalhost) {
          console.log('🔍 OpenAI deletion phase completed. Success:', openAIDeleteSuccess);
        }
      } else {
        if (isLocalhost) {
          console.log('📄 Not a file dataset, skipping OpenAI deletion');
        }
      }

      // STEP 2: Only delete from Directus if OpenAI deletion was successful OR if it's not a file dataset
      if (openAIDeleteSuccess || !isFileDatasetForDelete) {
        if (isLocalhost) {
          console.log('🗑️ STEP 2: Deleting from Directus...');
        }

        await axios.delete(`${DIRECTUS_DATASETS_ENDPOINT}/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (isLocalhost) {
          console.log('✅ Successfully deleted from Directus');
        }
        navigate('/datasets', {
          state: {
            snackbar: {
              type: 'success',
              message: t('datasets.deletedSuccess', { name: dataset.name })
            }
          }
        });

      } else {
        if (isLocalhost) {
          console.error('❌ OpenAI deletion failed, aborting Directus deletion to maintain data integrity');
        }
        const message = t('datasets.deleteOpenAIFailed');
        setError(message);
        setSnackbarState({ type: 'error', message });
        setLoading(false);
      }

    } catch (error) {
      if (window.location.hostname === 'localhost') {
        console.error('💥 Error deleting dataset:', error);
      }
      const message = t('datasets.deleteFailed', { error: error?.message || t('common.error_generic') });
      setError(message);
      setSnackbarState({ type: 'error', message });
      setLoading(false);
    }
  };

  const confirmDeleteDataset = async () => {
    setShowDeleteModal(false);
    await handleDelete();
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const response = await axios.get(`${DIRECTUS_INSTANCE}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file');
    }
  };

  const handleDownloadConfig = (config, filename) => {
    try {
      const dataStr = JSON.stringify(config, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading configuration:', error);
      setError('Failed to download configuration');
    }
  };

  const handleDownloadAllFiles = async () => {
    if (dataset.file && dataset.file.length > 0) {
      for (const fileRelation of dataset.file) {
        const directusFile = fileRelation.directus_files_id;
        await handleDownloadFile(directusFile.id, directusFile.filename_download);
        // Add small delay between downloads to prevent overwhelming the browser
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  };

  const goBack = () => {
    navigate('/datasets');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Byte';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return formatDisplayDateTime(dateString, localStorage.getItem('i18nextLng') || 'en');
  };

  // Dictionary for data types
  const getDataTypeLabel = (type) => {
    const typeLabels = {
      'user_interview_transcript': 'User Interview Transcript',
      'product_brochure': 'Product Brochure',
      'blog_post_article': 'Article or Blogpost',
      'case_study': 'Case Study',
      'white_paper': 'White Paper',
      'technical_documentation': 'Technical Documentation',
      'research_paper': 'Research Paper',
      'customer_feedback': 'Customer Feedback',
      'market_research': 'Market Research',
      'social_media_content': 'Social Media Content',
      'educational_material': 'Educational Material',
      'database': 'Database',
      'external_api': 'External API',
      'wordpress': 'WordPress',
      'other': 'Other'
    };
    return typeLabels[type] || type || 'Unknown';
  };

  // Removed unused getSourceIcon function

  const getSourceTypeLabel = (datasetType) => {
    switch (datasetType) {
      case 'file_upload':
        return 'File Upload';
      case 'wordpress':
        return 'WordPress Website';
      case 'external_api':
        return 'External API';
      default:
        // Try to infer from dataset content
        if (dataset?.file && dataset.file.length > 0) {
          return 'File Upload';
        }
        if (dataset?.api_config) {
          return 'External API';
        }
        if (dataset?.wp_config) {
          return 'WordPress Website';
        }
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className=" ">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className=" ">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-red-500 text-lg">{error}</div>
            <Button variant="secondary" onClick={goBack} className="mt-4">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!dataset) {
    return (
      <div className=" ">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-gray-500 text-lg">Dataset not found</div>
            <Button variant="secondary" onClick={goBack} className="mt-4">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className=" ">
      {/* TopBar */}
      <TopBar
        onGoBack={goBack}
        showSaveStatus={false}
        mode="back-only"
      />

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Title and Description */}
        {isEditing ? (
          <div className="mb-8 flex items-center space-x-3">
            <input
              type="text"
              value={newDatasetName}
              onChange={(e) => setNewDatasetName(e.target.value)}
              className="text-3xl font-medium text-neutral-900 bg-transparent border-b-2 border-primary-500 focus:outline-none focus:border-primary-700 px-2"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleUpdateName();
                } else if (e.key === 'Escape') {
                  setIsEditing(false);
                  setNewDatasetName(dataset.name);
                }
              }}
              autoFocus
            />
            <Button variant="primary" size="sm" onClick={handleUpdateName}>
              Save
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setIsEditing(false);
                setNewDatasetName(dataset.name);
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="mb-8">
            <h1 className="text-3xl font-medium text-gray-900">{dataset.name}</h1>
            <p className="text-sm text-gray-500 mt-2">
              {getDataTypeLabel(dataset.type)} • {getSourceTypeLabel(dataset.dataset_type)}
            </p>
          </div>
        )}

        {/* Single Column Layout */}
        <div className="space-y-6">
          {/* Dataset Information */}
          <div className="bg-white border border-gray-200 rounded-lg max-w-7xl mx-auto">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Dataset Information</h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Data Type</dt>
                    <dd className="mt-1 text-sm text-gray-900">{getDataTypeLabel(dataset.type)}</dd>
                  </div>

                  <div>
                    <dt className="text-sm font-medium text-gray-500">Source Type</dt>
                    <dd className="mt-1 text-sm text-gray-900">{getSourceTypeLabel(dataset.dataset_type)}</dd>
                  </div>

                  <div>
                    <dt className="text-sm font-medium text-gray-500">Provider</dt>
                    <dd className="mt-1 text-sm text-gray-900">{getProviderLabel(dataset)}</dd>
                  </div>

                  <div>
                    <dt className="text-sm font-medium text-gray-500">Availability</dt>
                    <dd className="mt-1 text-sm text-gray-900">{getAvailabilityLabel(dataset)}</dd>
                  </div>

                  <div>
                    <dt className="text-sm font-medium text-gray-500">Created</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(dataset.date_created)}</dd>
                  </div>

                  <div>
                    <dt className="text-sm font-medium text-gray-500">Created By</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {dataset.user_created?.first_name} {dataset.user_created?.last_name}
                    </dd>
                  </div>
                </div>

                {dataset.vector_store_batch_id && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Processing Status</dt>
                    <dd className="mt-1 text-sm text-gray-900">Vector Store Batch Created</dd>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Associated Files Section */}
          {((dataset.dataset_type === 'file_upload' ||
            (!dataset.dataset_type && dataset.file && dataset.file.length > 0) ||
            (dataset.dataset_type === null && dataset.file && dataset.file.length > 0)) &&
            dataset.file && dataset.file.length > 0) && (
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Associated Files</h3>
                    {dataset.file && dataset.file.length > 1 && (
                      <Button
                        variant="secondary"
                        size="sm"
                        shape="square"
                        onClick={handleDownloadAllFiles}
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                        Download All
                      </Button>
                    )}
                  </div>

                  <StandardTable
                    className="mt-0"
                    columns={[
                      {
                        key: 'filename',
                        title: 'File',
                        render: (row) => (
                          <span className="font-medium text-gray-900 truncate block max-w-xs" title={row.filename}>
                            {row.filename}
                          </span>
                        ),
                        cellClassName: 'whitespace-nowrap px-3 py-4 text-sm text-gray-500'
                      },
                      {
                        key: 'size',
                        title: 'Size',
                        cellClassName: 'whitespace-nowrap px-3 py-4 text-sm text-gray-500'
                      },
                      {
                        key: 'type',
                        title: 'Type',
                        render: (row) => (
                          <span className="truncate block max-w-[200px]" title={row.type}>
                            {row.type}
                          </span>
                        ),
                        cellClassName: 'whitespace-nowrap px-3 py-4 text-sm text-gray-500'
                      },
                      {
                        key: 'status',
                        title: 'Status',
                        render: (row) => (
                          <span className={row.status === 'Processed' ? 'text-emerald-600' : row.status === 'Processing...' ? 'text-amber-600' : 'text-gray-400'}>
                            {row.status}
                          </span>
                        ),
                        cellClassName: 'whitespace-nowrap px-3 py-4 text-sm text-gray-500'
                      }
                    ]}
                    data={dataset.file.map((fileRelation, index) => {
                      const directusFile = fileRelation.directus_files_id;
                      const openAIFile = openAIFiles.find(
                        (oai) => oai && oai.filename === directusFile.filename_download
                      );

                      return {
                        id: directusFile.id || index,
                        filename: directusFile.filename_download || directusFile.title,
                        size: formatFileSize(directusFile.filesize),
                        type: directusFile.type || '-',
                        status: openAIFile
                          ? (openAIFile.status === 'processed' ? 'Processed' : 'Processing...')
                          : '-',
                        actions: (
                          <Button
                            type="button"
                            variant="secondary"
                            size="xs"
                            shape="square"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadFile(directusFile.id, directusFile.filename_download);
                            }}
                          >
                            <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
                            Download
                          </Button>
                        )
                      };
                    })}
                  />
                </div>
              </div>
            )}

          {/* Configuration Details */}
          {(dataset.dataset_type === 'external_api' || (!dataset.dataset_type && dataset.api_config)) && dataset.api_config && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                    <CodeBracketIcon className="h-5 w-5 mr-2 text-green-600" />
                    API Configuration
                  </h3>
                  <div className="flex items-center space-x-2">
                    {!isEditingApiConfig && (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          shape="square"
                          onClick={handleStartEditingApiConfig}
                        >
                          <PencilIcon className="h-4 w-4 mr-1" />
                          Edit Config
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          shape="square"
                          onClick={() => handleDownloadConfig(typeof dataset.api_config === 'string' ? JSON.parse(dataset.api_config) : dataset.api_config, `${dataset.name}-api-config.json`)}
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                          Download Config
                        </Button>
                      </>
                    )}
                    {isEditingApiConfig && (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          shape="square"
                          onClick={handleUpdateApiConfig}
                        >
                          Save Changes
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          shape="square"
                          onClick={handleCancelEditingApiConfig}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {(() => {
                  const apiConfig = isEditingApiConfig ? editedApiConfig : (typeof dataset.api_config === 'string' ? JSON.parse(dataset.api_config) : dataset.api_config);
                  if (isEditingApiConfig) {
                    return (
                      <div className="space-y-6">
                        {/* Basic Configuration */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Function Name</label>
                            <input
                              type="text"
                              value={apiConfig.functionName || ''}
                              onChange={(e) => handleApiConfigChange('functionName', e.target.value)}
                              className="form-input"
                              placeholder="e.g., get_weather_data"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                            <Select
                              value={apiConfig.method || 'GET'}
                              onChange={(e) => handleApiConfigChange('method', e.target.value)}
                              className="form-input"
                            >
                              <option value="GET">GET</option>
                              <option value="POST">POST</option>
                              <option value="PUT">PUT</option>
                              <option value="DELETE">DELETE</option>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <textarea
                            value={apiConfig.description || ''}
                            onChange={(e) => handleApiConfigChange('description', e.target.value)}
                            rows={3}
                            className="form-input"
                            placeholder="Describe what this API function does..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">API URL</label>
                          <input
                            type="url"
                            value={apiConfig.url || ''}
                            onChange={(e) => handleApiConfigChange('url', e.target.value)}
                            className="form-input"
                            placeholder="https://api.example.com/endpoint"
                          />
                        </div>

                        {/* Tabs */}
                        <div className="border-t pt-4">
                          <div className="sm:hidden">
                            <label htmlFor="tabs" className="sr-only">Select a tab</label>
                            <Select
                              id="tabs"
                              value={activeTab}
                              onChange={(e) => setActiveTab(e.target.value)}
                              className="form-input"
                            >
                              {tabs.map((tab) => (
                                <option key={tab.id} value={tab.id}>{tab.name}</option>
                              ))}
                            </Select>
                          </div>
                          <div className="hidden sm:block">
                            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                              {tabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                  <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`${activeTab === tab.id
                                      ? 'border-primary-500 text-primary-600'
                                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                      } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                                  >
                                    <Icon className="h-4 w-4 mr-2" />
                                    {tab.name}
                                  </button>
                                );
                              })}
                            </nav>
                          </div>
                        </div>

                        {/* Tab Content */}
                        <div className="mt-6">
                          {activeTab === 'params' && (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-900">Parameters</h4>
                                <Button
                                  type="button"
                                  variant="primary-outline"
                                  size="xs"
                                  shape="square"
                                  onClick={addParameter}
                                >
                                  <PlusIcon className="h-3 w-3 mr-1" />
                                  Add Parameter
                                </Button>
                              </div>

                              {apiConfig.parameters && apiConfig.parameters.length > 0 ? (
                                <div className="space-y-3">
                                  {apiConfig.parameters.map((param, index) => (
                                    <div key={index} className="grid grid-cols-12 gap-2 items-end">
                                      <div className="col-span-3">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                                        <input
                                          type="text"
                                          value={param.name || ''}
                                          onChange={(e) => handleParameterChange(index, 'name', e.target.value)}
                                          className="form-input py-1.5 px-2 text-sm"
                                          placeholder="param_name"
                                        />
                                      </div>
                                      <div className="col-span-3">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Value</label>
                                        <input
                                          type="text"
                                          value={param.value || ''}
                                          onChange={(e) => handleParameterChange(index, 'value', e.target.value)}
                                          className="form-input py-1.5 px-2 text-sm"
                                          placeholder="default_value"
                                        />
                                      </div>
                                      <div className="col-span-4">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                                        <input
                                          type="text"
                                          value={param.description || ''}
                                          onChange={(e) => handleParameterChange(index, 'description', e.target.value)}
                                          className="form-input py-1.5 px-2 text-sm"
                                          placeholder="Parameter description"
                                        />
                                      </div>
                                      <div className="col-span-1 flex items-center">
                                        <label className="flex items-center">
                                          <input
                                            type="checkbox"
                                            checked={param.required || false}
                                            onChange={(e) => handleParameterChange(index, 'required', e.target.checked)}
                                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                          />
                                          <span className="ml-1 text-xs text-gray-700">Req</span>
                                        </label>
                                      </div>
                                      <div className="col-span-1">
                                        <button
                                          type="button"
                                          onClick={() => removeParameter(index)}
                                          className="p-1 text-red-600 hover:text-red-800"
                                        >
                                          <XMarkIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500">No parameters defined.</p>
                              )}
                            </div>
                          )}

                          {activeTab === 'auth' && (
                            <div className="space-y-4">
                              <h4 className="text-sm font-medium text-gray-900">Authentication</h4>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Auth Type</label>
                                <Select
                                  value={apiConfig.auth?.type || 'none'}
                                  onChange={(e) => handleAuthChange('type', e.target.value)}
                                  className="form-input"
                                >
                                  <option value="none">No Authentication</option>
                                  <option value="bearer">Bearer Token</option>
                                  <option value="basic">Basic Auth</option>
                                  <option value="api-key">API Key</option>
                                </Select>
                              </div>

                              {apiConfig.auth?.type === 'bearer' && (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <label className="block text-sm font-medium text-gray-700">Bearer Token</label>
                                    <button
                                      type="button"
                                      onClick={() => setShowSecrets(!showSecrets)}
                                      className="text-xs text-primary-600 hover:text-primary-500 flex items-center"
                                    >
                                      {showSecrets ? <EyeSlashIcon className="h-3 w-3 mr-1" /> : <EyeIcon className="h-3 w-3 mr-1" />}
                                      {showSecrets ? 'Hide' : 'Show'}
                                    </button>
                                  </div>
                                  <input
                                    type={showSecrets ? 'text' : 'password'}
                                    value={apiConfig.auth.token || ''}
                                    onChange={(e) => handleAuthChange('token', e.target.value)}
                                    className="form-input mb-1"
                                    placeholder="Enter bearer token"
                                  />
                                  <p className="text-[10px] text-gray-400">Leave as dots to keep existing secret.</p>
                                </div>
                              )}

                              {apiConfig.auth?.type === 'basic' && (
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                                    <input
                                      type="text"
                                      value={apiConfig.auth.username || ''}
                                      onChange={(e) => handleAuthChange('username', e.target.value)}
                                      className="form-input"
                                      placeholder="Username"
                                    />
                                  </div>
                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <label className="block text-sm font-medium text-gray-700">Password</label>
                                      <button
                                        type="button"
                                        onClick={() => setShowSecrets(!showSecrets)}
                                        className="text-xs text-primary-600 hover:text-primary-500 flex items-center"
                                      >
                                        {showSecrets ? <EyeSlashIcon className="h-3 w-3 mr-1" /> : <EyeIcon className="h-3 w-3 mr-1" />}
                                        {showSecrets ? 'Hide' : 'Show'}
                                      </button>
                                    </div>
                                    <input
                                      type={showSecrets ? 'text' : 'password'}
                                      value={apiConfig.auth.password || ''}
                                      onChange={(e) => handleAuthChange('password', e.target.value)}
                                      className="form-input"
                                      placeholder="Password"
                                    />
                                    <p className="text-[10px] text-gray-400 mt-1">Leave as dots to keep existing password.</p>
                                  </div>
                                </div>
                              )}

                              {apiConfig.auth?.type === 'api-key' && (
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <label className="block text-sm font-medium text-gray-700">API Key</label>
                                      <button
                                        type="button"
                                        onClick={() => setShowSecrets(!showSecrets)}
                                        className="text-xs text-primary-600 hover:text-primary-500 flex items-center"
                                      >
                                        {showSecrets ? <EyeSlashIcon className="h-3 w-3 mr-1" /> : <EyeIcon className="h-3 w-3 mr-1" />}
                                        {showSecrets ? 'Hide' : 'Show'}
                                      </button>
                                    </div>
                                    <input
                                      type={showSecrets ? 'text' : 'password'}
                                      value={apiConfig.auth.apiKey || ''}
                                      onChange={(e) => handleAuthChange('apiKey', e.target.value)}
                                      className="form-input"
                                      placeholder="Enter API key"
                                    />
                                    <p className="text-[10px] text-gray-400 mt-1">Leave as dots to keep existing key.</p>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Header Name</label>
                                    <input
                                      type="text"
                                      value={apiConfig.auth.apiKeyHeader || 'X-API-Key'}
                                      onChange={(e) => handleAuthChange('apiKeyHeader', e.target.value)}
                                      className="form-input"
                                      placeholder="X-API-Key"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {activeTab === 'headers' && (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-900">Custom Headers</h4>
                                <Button
                                  type="button"
                                  variant="primary-outline"
                                  size="xs"
                                  shape="square"
                                  onClick={addHeader}
                                >
                                  <PlusIcon className="h-3 w-3 mr-1" />
                                  Add Header
                                </Button>
                              </div>

                              {apiConfig.headers && apiConfig.headers.length > 0 ? (
                                <div className="space-y-3">
                                  {apiConfig.headers.map((header, index) => (
                                    <div key={index} className="grid grid-cols-12 gap-2 items-end">
                                      <div className="col-span-5">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Header Name</label>
                                        <input
                                          type="text"
                                          value={header.name || ''}
                                          onChange={(e) => handleHeaderChange(index, 'name', e.target.value)}
                                          className="form-input py-1.5 px-2 text-sm"
                                          placeholder="Header-Name"
                                        />
                                      </div>
                                      <div className="col-span-6">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Header Value</label>
                                        <input
                                          type="text"
                                          value={header.value || ''}
                                          onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                                          className="form-input py-1.5 px-2 text-sm"
                                          placeholder="header-value"
                                        />
                                      </div>
                                      <div className="col-span-1">
                                        <button
                                          type="button"
                                          onClick={() => removeHeader(index)}
                                          className="p-1 text-red-600 hover:text-red-800"
                                        >
                                          <XMarkIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500">No custom headers defined.</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Function Name</dt>
                            <dd className="text-sm text-gray-900 font-mono">{apiConfig.functionName}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Method</dt>
                            <dd className="text-sm text-gray-900">{apiConfig.method}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Response Format</dt>
                            <dd className="text-sm text-gray-900">{apiConfig.responseFormat || 'JSON'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Connection Status</dt>
                            <dd className="text-sm text-gray-900">Connected</dd>
                          </div>
                        </div>

                        <div>
                          <dt className="text-xs font-medium text-gray-500">Description</dt>
                          <dd className="text-sm text-gray-900">{apiConfig.description}</dd>
                        </div>

                        <div>
                          <dt className="text-xs font-medium text-gray-500">Base URL</dt>
                          <dd className="text-sm text-gray-900 break-all font-mono">{apiConfig.baseUrl}</dd>
                        </div>

                        <div>
                          <dt className="text-xs font-medium text-gray-500">Endpoint</dt>
                          <dd className="text-sm text-gray-900 font-mono">{apiConfig.endpoint}</dd>
                        </div>

                        {apiConfig.parameters && apiConfig.parameters.length > 0 && (
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Parameters</dt>
                            <dd className="text-sm text-gray-900">
                              <div className="mt-1 space-y-1">
                                {apiConfig.parameters.map((param, index) => (
                                  <div key={index} className="flex items-center gap-2">
                                    <span className="font-mono text-xs">{param.name}</span>
                                    <span className="text-xs text-gray-500">({param.type})</span>
                                    {param.required && <span className="text-xs text-red-600">Required</span>}
                                    {param.description && <span className="text-xs text-gray-500">- {param.description}</span>}
                                  </div>
                                ))}
                              </div>
                            </dd>
                          </div>
                        )}
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          )}

          {/* WordPress Configuration */}
          {dataset.dataset_type === 'wordpress' && dataset.wp_config && (
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                    <GlobeAltIcon className="h-5 w-5 mr-2 text-purple-600" />
                    WordPress Configuration
                  </h3>
                  <div className="flex items-center space-x-2">
                    {!isEditingWpConfig && (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          shape="square"
                          onClick={handleStartEditingWpConfig}
                        >
                          <PencilIcon className="h-4 w-4 mr-1" />
                          Edit Config
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          shape="square"
                          onClick={() => handleDownloadConfig(JSON.parse(dataset.wp_config), `${dataset.name}-wordpress-config.json`)}
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                          Download Config
                        </Button>
                      </>
                    )}
                    {isEditingWpConfig && (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          shape="square"
                          onClick={handleUpdateWpConfig}
                        >
                          Save Changes
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          shape="square"
                          onClick={handleCancelEditingWpConfig}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {(() => {
                  const wpConfig = isEditingWpConfig ? editedWpConfig : JSON.parse(dataset.wp_config);
                  if (isEditingWpConfig) {
                    return (
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Site URL</label>
                            <input
                              type="url"
                              value={wpConfig.siteUrl || ''}
                              onChange={(e) => setEditedWpConfig({ ...wpConfig, siteUrl: e.target.value })}
                              className="form-input"
                              placeholder="https://yoursite.com"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                            <input
                              type="text"
                              value={wpConfig.username || ''}
                              onChange={(e) => setEditedWpConfig({ ...wpConfig, username: e.target.value })}
                              className="form-input"
                              placeholder="Username"
                            />
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-sm font-medium text-gray-700">Application Password</label>
                              <button
                                type="button"
                                onClick={() => setShowSecrets(!showSecrets)}
                                className="text-xs text-primary-600 hover:text-primary-500 flex items-center"
                              >
                                {showSecrets ? <EyeSlashIcon className="h-3 w-3 mr-1" /> : <EyeIcon className="h-3 w-3 mr-1" />}
                                {showSecrets ? 'Hide' : 'Show'}
                              </button>
                            </div>
                            <input
                              type={showSecrets ? 'text' : 'password'}
                              value={wpConfig.applicationPassword || ''}
                              onChange={(e) => setEditedWpConfig({ ...wpConfig, applicationPassword: e.target.value })}
                              className="form-input"
                              placeholder="Application Password"
                            />
                            <p className="text-[10px] text-gray-400 mt-1">Leave as dots to keep existing password.</p>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Post Types to Import</label>
                          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                            {['post', 'page', 'product', 'event', 'portfolio', 'testimonial'].map((postType) => (
                              <label key={postType} className="inline-flex items-center">
                                <input
                                  type="checkbox"
                                  checked={wpConfig.postTypes.includes(postType)}
                                  onChange={(e) => {
                                    const newPostTypes = e.target.checked
                                      ? [...wpConfig.postTypes, postType]
                                      : wpConfig.postTypes.filter(pt => pt !== postType);
                                    setEditedWpConfig({ ...wpConfig, postTypes: newPostTypes });
                                  }}
                                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                />
                                <span className="ml-2 text-sm text-gray-700 capitalize">{postType}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Username</dt>
                          <dd className="text-sm text-gray-900 font-mono">{wpConfig.username}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Authentication</dt>
                          <dd className="text-sm text-gray-900">Application Password Configured</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Post Types</dt>
                          <dd className="text-sm text-gray-900">{wpConfig.postTypes.length} types selected</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Connection Status</dt>
                          <dd className="text-sm text-gray-900">Connected</dd>
                        </div>
                      </div>

                      <div>
                        <dt className="text-xs font-medium text-gray-500">Site URL</dt>
                        <dd className="text-sm text-gray-900 break-all font-mono">{wpConfig.siteUrl}</dd>
                      </div>

                      <div>
                        <dt className="text-xs font-medium text-gray-500">Post Types to Import</dt>
                        <dd className="text-sm text-gray-900">
                          {wpConfig.postTypes.join(', ')}
                        </dd>
                      </div>

                      {wpConfig.categories && wpConfig.categories.length > 0 && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Categories</dt>
                          <dd className="text-sm text-gray-900">{wpConfig.categories.join(', ')}</dd>
                        </div>
                      )}

                      {wpConfig.tags && wpConfig.tags.length > 0 && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500">Tags</dt>
                          <dd className="text-sm text-gray-900">{wpConfig.tags.join(', ')}</dd>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Debug Information */}
          {window.location.hostname === 'localhost' && dataset.vector_store_batch_id && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-yellow-900 mb-4">Debug Information (Localhost Only)</h3>
                <div>
                  <dt className="text-xs font-medium text-yellow-700">Vector Store Batch ID</dt>
                  <dd className="text-sm text-yellow-900 font-mono break-all">{dataset.vector_store_batch_id}</dd>
                </div>
              </div>
            </div>
          )}

          <div className="pt-2 pb-8">
            <Button
              variant="danger"
              size="sm"
              shape="rounded"
              onClick={() => setShowDeleteModal(true)}
              disabled={loading}
            >
              <TrashIcon className="h-4 w-4 mr-2" aria-hidden="true" />
              Remove Dataset
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Dataset Confirmation Modal */}
      <DeleteDatasetConfirmationModal
        open={showDeleteModal}
        setOpen={setShowDeleteModal}
        onConfirm={confirmDeleteDataset}
        isDeleting={loading}
        datasetName={dataset?.name || 'this dataset'}
      />
      {snackbarState && (
        <Snackbar
          message={snackbarState.message}
          type={snackbarState.type}
          actionName={t('common.close', { defaultValue: 'Close' })}
          onClose={() => setSnackbarState(null)}
        />
      )}
    </div>
  );
}

export default DatasetDetails; 